// The yard gate: every truck is recorded when it comes in and when it leaves, so we can see how long it took to get fuel and go,
// or how long the workshop kept it. The board shows who is in right now and for how long.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Field, TextIn, Loading, Pill } from "@/components/kit";
import api from "@/lib/api";
import { act, useLoad, useCan, useRegisters, Select, Num, n, opts } from "@/components/erp";

const PURPOSES = [["fuel", "Fuel"], ["workshop", "Workshop (service / repair)"], ["tyres", "Tyres"], ["offload", "Offloading / loading"], ["waiting", "Waiting for a load"], ["parking", "Parked / overnight"], ["other", "Other"]];
const LEVELS = [["full", "Full"], ["three_quarter", "Three quarters"], ["half", "Half"], ["quarter", "A quarter"], ["empty", "Empty / reserve"]].map(([value, label]) => ({ value, label }));
const GOING = [["trip", "On a trip"], ["parked", "Parked elsewhere"], ["other", "Other"]].map(([value, label]) => ({ value, label }));

export const dur = (m) => (m == null ? "—" : m >= 1440 ? `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h` : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`);
const clock = (iso) => (iso ? new Date(iso).toLocaleString("en-KE", { timeZone: "Africa/Nairobi", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Yard() {
  const can = useCan();
  const [tab, setTab] = useState("board");
  const tabs = [{ value: "board", label: "In the yard now" }, { value: "history", label: "Gate log" }, { value: "report", label: "How long it takes" }];
  return (
    <Page title="Yard gate">
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === "board" && <Board can={can} />}
      {tab === "history" && <History />}
      {tab === "report" && <Report />}
    </Page>
  );
}

function Board({ can }) {
  const { data, reload } = useLoad("/yard/board");
  const regs = useRegisters(["trucks"]);
  const go = useNavigate();
  const [f, setF] = useState(null);
  const [o, setO] = useState(null);
  if (!data) return <Loading />;
  const checkIn = async () => {
    const body = { truck_id: f.truck_id, driver: f.driver || "", odometer: n(f.odometer) || undefined, fuel_level: f.fuel_level || "", purposes: f.purposes || [], note: f.note || "", at: f.late ? f.at : undefined };
    if (await act(() => api.post("/yard/in", body), "Truck recorded in the yard")) { setF(null); reload(); }
  };
  const checkOut = async () => { if (await act(() => api.post(`/yard/${o.id}/out`, { odometer: n(o.odometer) || undefined, going: o.going || "trip", note: o.note || "" }), "Truck recorded out")) { setO(null); reload(); } };
  const toggle = (k) => setF({ ...f, purposes: (f.purposes || []).includes(k) ? f.purposes.filter((x) => x !== k) : [...(f.purposes || []), k] });
  return (
    <>
      {can("yard.log") && !f && <div className="mt-3"><Btn kind="primary" onClick={() => setF({ purposes: [] })}>+ Truck coming in</Btn></div>}
      {f && (
        <Card title="Truck coming in" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Truck"><Select value={f.truck_id} onChange={(v) => setF({ ...f, truck_id: v })} options={opts(regs.trucks, "plate")} /></Field>
            <Field label="Driver"><TextIn value={f.driver} onChange={(v) => setF({ ...f, driver: v })} /></Field>
            <Field label="Odometer"><Num value={f.odometer} onChange={(v) => setF({ ...f, odometer: v })} /></Field>
            <Field label="Fuel in the tank"><Select value={f.fuel_level} options={LEVELS} onChange={(v) => setF({ ...f, fuel_level: v })} /></Field>
          </div>
          <Field label="What is it here for? (tick all that apply)">
            <div className="flex flex-wrap gap-3 text-sm">{PURPOSES.map(([k, l]) => <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={(f.purposes || []).includes(k)} onChange={() => toggle(k)} />{l}</label>)}</div>
          </Field>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            <Field label="Note (anything wrong you noticed)"><TextIn value={f.note} onChange={(v) => setF({ ...f, note: v })} /></Field>
            <div className="text-sm"><label className="flex items-center gap-2"><input type="checkbox" checked={!!f.late} onChange={(e) => setF({ ...f, late: e.target.checked })} />It came in earlier - enter the real time</label>
              {f.late && <input type="datetime-local" className="mt-1 rounded border border-slate-300 px-2 py-1" value={f.at || ""} onChange={(e) => setF({ ...f, at: e.target.value })} />}</div>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={checkIn}>Record it in</Btn><Btn onClick={() => setF(null)}>Cancel</Btn></div>
        </Card>)}
      {o && (
        <Card title={`${o.truck} leaving`} className="mt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Odometer"><Num value={o.odometer} onChange={(v) => setO({ ...o, odometer: v })} /></Field>
            <Field label="Going"><Select empty={null} value={o.going || "trip"} options={GOING} onChange={(v) => setO({ ...o, going: v })} /></Field>
            <Field label="Note"><TextIn value={o.note} onChange={(v) => setO({ ...o, note: v })} /></Field>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={checkOut}>Record it out</Btn><Btn onClick={() => setO(null)}>Cancel</Btn></div>
        </Card>)}
      <div className="mt-3">
        <Table minWidth={760} cols={[{ label: "Truck" }, { label: "Came in" }, { label: "In yard for" }, { label: "For" }, { label: "Fuel" }, { label: "Work" }, { label: "" }]}>
          {data.map((v) => (
            <Tr key={v.id}>
              <Td><b>{v.truck}</b><div className="text-xs text-slate-500">{v.driver}</div></Td>
              <Td>{clock(v.in_at)}{v.late_entry && <div className="text-xs text-amber-700">late entry</div>}</Td>
              <Td><b>{dur(v.minutes_in)}</b>{v.flags.map((x, i) => <div key={i} className="text-xs text-red-600">{x}</div>)}</Td>
              <Td>{v.purpose_labels.join(", ")}</Td>
              <Td>{v.fuel_minutes != null ? <Pill kind="green">{`fuelled after ${dur(v.fuel_minutes)}`}</Pill> : v.waiting_fuel ? <Pill kind="amber">waiting for fuel</Pill> : "—"}</Td>
              <Td>{v.cards.length ? v.cards.map((c) => <div key={c.no} className="text-xs">{c.no} {c.open ? <Pill kind="amber">{`open · ${dur(c.minutes)}`}</Pill> : <Pill kind="green">{`done in ${dur(c.minutes)}`}</Pill>}</div>) : "—"}</Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {can("jobcard.open") && <Btn onClick={() => go(`/jobcards?truck=${v.truck_id}`)}>Needs work</Btn>}
                  {can("yard.log") && v.waiting_fuel && <Btn onClick={async () => { if (await act(() => api.post(`/yard/${v.id}/fuelled`), "Fuel noted")) reload(); }}>Fuel taken elsewhere</Btn>}
                  {can("yard.log") && <Btn onClick={() => setO({ id: v.id, truck: v.truck })}>Leaving</Btn>}
                </div>
              </Td>
            </Tr>))}
        </Table>
        {!data.length && <p className="mt-2 text-sm text-slate-500">No truck is in the yard right now.</p>}
      </div>
      <p className="mt-3 text-xs text-slate-500">Order in the yard: work done, then the trip dispatched, then the depot fuels it, then it goes out through the gate. A truck with an open job card can't be dispatched, fuelled for a trip or checked out.</p>
    </>
  );
}

function History() {
  const [truck, setTruck] = useState("");
  const regs = useRegisters(["trucks"]);
  const { data } = useLoad("/yard", { truck_id: truck || undefined });
  return (
    <>
      <div className="mt-3 max-w-xs"><Select empty="Every truck" value={truck} onChange={(v) => setTruck(v || "")} options={opts(regs.trucks, "plate")} /></div>
      {!data ? <Loading /> : (
        <Table minWidth={760} cols={[{ label: "Truck" }, { label: "In" }, { label: "Out" }, { label: "Time in yard" }, { label: "For" }, { label: "Fuel took" }, { label: "Job cards" }]}>
          {data.map((v) => (
            <Tr key={v.id}><Td><b>{v.truck}</b><div className="text-xs text-slate-500">{v.driver}</div></Td><Td>{clock(v.in_at)}</Td><Td>{v.status === "in" ? <Pill kind="amber">in yard</Pill> : clock(v.out_at)}</Td>
              <Td>{dur(v.minutes_in)}{v.flags.map((x, i) => <div key={i} className="text-xs text-red-600">{x}</div>)}</Td><Td>{v.purpose_labels.join(", ")}</Td><Td>{dur(v.fuel_minutes)}</Td><Td>{v.cards.map((c) => c.no).join(", ") || "—"}</Td></Tr>))}
        </Table>)}
    </>
  );
}

function Report() {
  const { data } = useLoad("/yard/report");
  if (!data) return <Loading />;
  return (
    <>
      <h3 className="mt-4 text-sm font-semibold text-slate-700">By reason for coming in</h3>
      <Table minWidth={560} cols={[{ label: "Reason" }, { label: "Visits", align: "right" }, { label: "Average time in yard", align: "right" }, { label: "Longest", align: "right" }, { label: "Average time to get fuel", align: "right" }]}>
        {data.by_purpose.map((r) => <Tr key={r.purpose}><Td>{r.label}</Td><Td right>{r.visits}</Td><Td right>{dur(r.avg_min)}</Td><Td right>{dur(r.longest_min)}</Td><Td right>{dur(r.avg_fuel_min)}</Td></Tr>)}
      </Table>
      <h3 className="mt-4 text-sm font-semibold text-slate-700">By truck (who spends longest in the yard)</h3>
      <Table minWidth={420} cols={[{ label: "Truck" }, { label: "Visits", align: "right" }, { label: "Total time", align: "right" }, { label: "Average", align: "right" }]}>
        {data.by_truck.map((t) => <Tr key={t.truck}><Td>{t.truck}</Td><Td right>{t.visits}</Td><Td right>{dur(t.total_min)}</Td><Td right>{dur(t.avg_min)}</Td></Tr>)}
      </Table>
      {!data.by_purpose.length && <p className="text-sm text-slate-500">Nothing to report until trucks have been recorded out.</p>}
    </>
  );
}
